/**
 * sales.repository.ts — 영업(sales_*) Firestore 격리 layer (read-only).
 *
 *   - watchInvoicesBetween: 매출전표를 거래일 [from,to] 범위로 실시간 구독.
 *     date 는 UTC 자정 저장이므로 `${day}T00:00:00.000Z` 경계로 정확히 inclusive.
 *     같은 필드(date) range + orderBy → 복합 인덱스 불필요.
 *   - 마스터(거래처/현장/담당자)는 자주 안 바뀌므로 1회 fetch + 모듈 캐시.
 */
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { tcol } from "@/domain/tenantPath";
import {
  allocationFromFirestore,
  balanceFromFirestore,
  customerDetailFromData,
  customerFromFirestore,
  invoiceFromFirestore,
  receiptFromFirestore,
  repFromFirestore,
  siteFromFirestore,
  type SalesAllocation,
  type SalesBalance,
  type SalesCustomer,
  type SalesCustomerDetail,
  type SalesInvoice,
  type SalesReceipt,
  type SalesRep,
  type SalesSite,
} from "@/domain/sales";

const INVOICES = "sales_invoices";
const RECEIPTS = "sales_receipts";
const CUSTOMERS = "sales_customers";
const SITES = "sales_sites";
const REPS = "sales_reps";
const BALANCES = "sales_balances";
const ALLOCATIONS = "sales_allocations";
const SYNC_OPS = "sales_sync_ops";
const SYNC_STATUS = "sales_sync_status";

/** YYYY-MM-DD → 거래일 경계 Timestamp (UTC 자정) */
function dayBoundary(day: string): Timestamp {
  return Timestamp.fromDate(new Date(`${day}T00:00:00.000Z`));
}

// 마스터 캐시 (모듈 수명 동안 1회 로드)
let repsCache: SalesRep[] | null = null;
let customersCache: SalesCustomer[] | null = null;
let sitesCache: SalesSite[] | null = null;
const customerDetailCache = new Map<string, SalesCustomerDetail>();
let balancesCache: SalesBalance[] | null = null;
let allocationsCache: SalesAllocation[] | null = null;
/** 거래처별 매출·수금 세션 캐시 — from="" 은 전 기간 */
const activityCache = new Map<string, { from: string; inv: SalesInvoice[]; rcp: SalesReceipt[] }>();

export const salesRepo = {
  /** 거래일 [from,to] 범위 매출전표 실시간 구독. 정렬: date desc */
  watchInvoicesBetween(
    from: string,
    to: string,
    cb: (rows: SalesInvoice[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const q = query(
      collection(db, tcol(INVOICES)),
      where("date", ">=", dayBoundary(from)),
      where("date", "<=", dayBoundary(to)),
      orderBy("date", "desc"),
    );
    return onSnapshot(q, (snap) => cb(snap.docs.map(invoiceFromFirestore)), onError);
  },

  /** 거래일 [from,to] 범위 매출전표 일회성 조회 (홈 KPI 월별 집계용). 정렬 없음(클라 집계). */
  async fetchInvoicesBetween(from: string, to: string): Promise<SalesInvoice[]> {
    const q = query(
      collection(db, tcol(INVOICES)),
      where("date", ">=", dayBoundary(from)),
      where("date", "<=", dayBoundary(to)),
    );
    const snap = await getDocs(q);
    return snap.docs.map(invoiceFromFirestore);
  },

  /** 수금일 [from,to] 범위 수금전표 실시간 구독. 정렬: date desc */
  watchReceiptsBetween(
    from: string,
    to: string,
    cb: (rows: SalesReceipt[]) => void,
    onError?: (e: Error) => void,
  ): () => void {
    const q = query(
      collection(db, tcol(RECEIPTS)),
      where("date", ">=", dayBoundary(from)),
      where("date", "<=", dayBoundary(to)),
      orderBy("date", "desc"),
    );
    return onSnapshot(q, (snap) => cb(snap.docs.map(receiptFromFirestore)), onError);
  },

  /** 수금일 [from,to] 범위 수금전표 일회성 조회 (홈 KPI 월별 집계용). 정렬 없음(클라 집계). */
  async fetchReceiptsBetween(from: string, to: string): Promise<SalesReceipt[]> {
    const q = query(
      collection(db, tcol(RECEIPTS)),
      where("date", ">=", dayBoundary(from)),
      where("date", "<=", dayBoundary(to)),
    );
    const snap = await getDocs(q);
    return snap.docs.map(receiptFromFirestore);
  },

  /** 담당자 마스터 (8명) — 캐시 */
  async fetchReps(): Promise<SalesRep[]> {
    if (repsCache) return repsCache;
    const snap = await getDocs(collection(db, tcol(REPS)));
    repsCache = snap.docs.map(repFromFirestore).sort((a, b) => a.code.localeCompare(b.code));
    return repsCache;
  },

  /** 거래처 마스터 전체 (검색 모달용) — 캐시 */
  async fetchCustomers(): Promise<SalesCustomer[]> {
    if (customersCache) return customersCache;
    const snap = await getDocs(collection(db, tcol(CUSTOMERS)));
    customersCache = snap.docs.map(customerFromFirestore);
    return customersCache;
  },

  /** 전 현장 목록 (현장관리 화면). 4,861건 규모 — 세션 캐시로 재조회를 막는다 */
  async fetchAllSites(): Promise<SalesSite[]> {
    if (sitesCache) return sitesCache;
    const snap = await getDocs(collection(db, tcol(SITES)));
    sitesCache = snap.docs.map(siteFromFirestore);
    return sitesCache;
  },

  /** 특정 거래처의 현장 목록 (검색 모달 drill-down) */
  async fetchSitesByCustomer(customerCode: string): Promise<SalesSite[]> {
    const q = query(collection(db, tcol(SITES)), where("customer_code", "==", customerCode));
    const snap = await getDocs(q);
    return snap.docs.map(siteFromFirestore).sort((a, b) => a.siteName.localeCompare(b.siteName));
  },

  /** 현장 주소 단건 조회 — doc id = `${customer_code}_${site_code}` (ksms165 d16_addr).
   *  sales.view 권한 없는 사용자는 permission-denied → null 반환 (호출측 폴백용). */
  async fetchSiteAddress(customerCode: string, siteCode: string): Promise<string | null> {
    try {
      const snap = await getDoc(doc(db, tcol(SITES), `${customerCode}_${siteCode}`));
      if (!snap.exists()) return null;
      const addr = String(snap.data().address ?? "").trim();
      return addr.length > 0 ? addr : null;
    } catch {
      return null;
    }
  },

  /** 특정 거래처의 매출전표 단건 조회 (거래처 관리용). 정렬: date desc.
   *  from="" 이면 전 기간(customer_code 단일 조건), from 이 있으면 date >= from 을 함께 건다
   *  → customer_code + date 복합 인덱스 필요(firestore.indexes.json). */
  async fetchInvoicesByCustomer(customerCode: string, from = ""): Promise<SalesInvoice[]> {
    const q = from
      ? query(collection(db, tcol(INVOICES)), where("customer_code", "==", customerCode), where("date", ">=", dayBoundary(from)))
      : query(collection(db, tcol(INVOICES)), where("customer_code", "==", customerCode));
    const snap = await getDocs(q);
    return snap.docs.map(invoiceFromFirestore).sort((a, b) => b.date.localeCompare(a.date));
  },

  /** 특정 거래처의 수금전표 단건 조회. 정렬: date desc. from 규칙은 fetchInvoicesByCustomer 와 동일. */
  async fetchReceiptsByCustomer(customerCode: string, from = ""): Promise<SalesReceipt[]> {
    const q = from
      ? query(collection(db, tcol(RECEIPTS)), where("customer_code", "==", customerCode), where("date", ">=", dayBoundary(from)))
      : query(collection(db, tcol(RECEIPTS)), where("customer_code", "==", customerCode));
    const snap = await getDocs(q);
    return snap.docs.map(receiptFromFirestore).sort((a, b) => b.date.localeCompare(a.date));
  },

  /**
   * 거래처 매출·수금 묶음 조회 + 세션 캐시.
   * 캐시 키 = 거래처 코드, 값 = 받아온 하한(from)과 행들. 요청 하한이 캐시 하한보다 같거나 늦으면
   * (= 이미 받은 범위 안이면) 서버에 가지 않고 클라에서 자른다. 더 이른 하한(넓힘)이면 다시 받아 교체.
   * 관급(C023) 한 곳이 전표의 절반이라, 기본 기간을 좁혀 두면 클릭당 읽기가 수천 건 → 수백 건으로 준다.
   */
  async fetchCustomerActivity(customerCode: string, from = "", force = false): Promise<{ inv: SalesInvoice[]; rcp: SalesReceipt[] }> {
    const hit = activityCache.get(customerCode);
    const covers = hit && !force && (hit.from === "" || (from !== "" && hit.from <= from));
    if (hit && covers) {
      if (from === hit.from) return { inv: hit.inv, rcp: hit.rcp };
      return { inv: hit.inv.filter((r) => r.date >= from), rcp: hit.rcp.filter((r) => r.date >= from) };
    }
    try {
      const [inv, rcp] = await Promise.all([
        salesRepo.fetchInvoicesByCustomer(customerCode, from),
        salesRepo.fetchReceiptsByCustomer(customerCode, from),
      ]);
      activityCache.set(customerCode, { from, inv, rcp });
      return { inv, rcp };
    } catch (e) {
      // 복합 인덱스가 아직 빌드 중(배포 직후 몇 분)이면 failed-precondition. 옛 방식(전 기간 받기)으로 조용히 우회하고
      // 전 기간으로 캐시해 두면 이후 좁힘은 무요청. 인덱스가 준비되면 다음 세션부터 자동으로 좁은 쿼리를 쓴다.
      if (!from || (e as { code?: string }).code !== "failed-precondition") throw e;
      console.warn("[sales] customer_code+date 인덱스 미준비 — 전 기간 조회로 폴백", (e as Error).message);
      const [inv, rcp] = await Promise.all([
        salesRepo.fetchInvoicesByCustomer(customerCode),
        salesRepo.fetchReceiptsByCustomer(customerCode),
      ]);
      activityCache.set(customerCode, { from: "", inv, rcp });
      return { inv: inv.filter((r) => r.date >= from), rcp: rcp.filter((r) => r.date >= from) };
    }
  },

  /** 거래처 활동 캐시 비우기 — 수동 동기화 뒤 */
  clearActivityCache(): void {
    activityCache.clear();
  },

  /** 거래처 상세(ERP 연락처·주소·거래조건) 단건 로드 — doc id = customer_code. 코드별 캐시 */
  async fetchCustomerDetail(customerCode: string): Promise<SalesCustomerDetail | null> {
    const hit = customerDetailCache.get(customerCode);
    if (hit) return hit;
    const snap = await getDoc(doc(db, tcol(CUSTOMERS), customerCode));
    if (!snap.exists()) return null;
    const detail = customerDetailFromData(snap.data());
    customerDetailCache.set(customerCode, detail);
    return detail;
  },

  /** 거래처 미수 스냅샷 전체 (ERP 미수현황) — 캐시 (force 시 갱신) */
  async fetchBalances(force = false): Promise<SalesBalance[]> {
    if (balancesCache && !force) return balancesCache;
    const snap = await getDocs(collection(db, tcol(BALANCES)));
    balancesCache = snap.docs.map(balanceFromFirestore);
    return balancesCache;
  },

  /** 관급 현장별 잔량 전체 (배정−출하) — 캐시. 관급 거래처 ~14곳뿐 (force 시 갱신) */
  async fetchAllocations(force = false): Promise<SalesAllocation[]> {
    if (allocationsCache && !force) return allocationsCache;
    const snap = await getDocs(collection(db, tcol(ALLOCATIONS)));
    allocationsCache = snap.docs.map(allocationFromFirestore);
    return allocationsCache;
  },

  /** 마지막 동기화 결과 구독 (sales_sync_status/latest) */
  watchSyncStatus(cb: (s: SalesSyncStatus | null) => void): () => void {
    return onSnapshot(doc(db, tcol(SYNC_STATUS), "latest"), (snap) => {
      const d = snap.data();
      if (!d) { cb(null); return; }
      cb({
        ok: d.ok === true,
        source: typeof d.source === "string" ? d.source : "",
        mode: d.result?.mode ?? "",
        lastRunAt: d.last_run_at instanceof Timestamp ? d.last_run_at.toDate() : null,
        result: d.result ?? null,
        error: typeof d.error === "string" ? d.error : "",
      });
    });
  },

  /**
   * 수동 동기화 요청 — sales_sync_ops 문서 생성 → CF onCreate 처리 → 완료 대기.
   * 성공 시 잔량/미수/거래처 캐시 무효화(다음 fetch 갱신).
   */
  async requestSync(deep: boolean, timeoutMs = 180000): Promise<SalesSyncResultLite> {
    const user = auth.currentUser;
    if (!user) throw new Error("로그인이 필요합니다.");
    const ref = await addDoc(collection(db, tcol(SYNC_OPS)), {
      action: "sync", deep, status: "pending",
      requested_by: user.uid, created_at: serverTimestamp(),
    });
    return await new Promise<SalesSyncResultLite>((resolve, reject) => {
      const tid = setTimeout(() => { unsub(); reject(new Error("동기화 타임아웃")); }, timeoutMs);
      const unsub = onSnapshot(ref, (snap) => {
        const d = snap.data();
        if (!d) return;
        if (d.status === "done") {
          clearTimeout(tid); unsub();
          allocationsCache = null; balancesCache = null; customersCache = null; customerDetailCache.clear();
          resolve((d.result ?? {}) as SalesSyncResultLite);
        } else if (d.status === "error") {
          clearTimeout(tid); unsub();
          reject(new Error(typeof d.error === "string" ? d.error : "동기화 실패"));
        }
      }, (e) => { clearTimeout(tid); reject(e); });
    });
  },
};

export interface SalesSyncResultLite {
  mode?: string; cutoff?: string; invoices?: number; receipts?: number; masters?: number; allocations?: number; elapsed_ms?: number;
}
export interface SalesSyncStatus {
  ok: boolean; source: string; mode: string; lastRunAt: Date | null; result: SalesSyncResultLite | null; error: string;
}

// ── 개인 표시 설정 (users/{uid}/preferences/sales) ─────────────────────
// quickPresets: 5개 빠른 조회기간 버튼 구성 (PeriodKey 문자열 배열). 검증은 화면(salesPeriods)에서.
// *Period: 탭별 기본 조회기간. 값 검증은 salesPeriods 의 periodRange / normalizeCustomerPeriod 폴백에 맡긴다.
export interface SalesPrefs {
  defaultView: "customer" | "site";  // 기간별조회 기본 보기
  defaultPeriod: string;             // 기간별조회 기본 조회기간 (PeriodKey)
  monthlyPeriod: string;             // 월판매현황 기본 조회기간 (PeriodKey)
  receiptsPeriod: string;            // 수금조회 기본 조회기간 (PeriodKey)
  customerPeriod: string;            // 거래처 관리 기본 기간 — 사급 (CustomerPeriodKey)
  customerPeriodGov: string;         // 거래처 관리 기본 기간 — 관급 (CustomerPeriodKey). 관급은 전표가 많아 기본 최근 2년
  quickPresets: string[];            // 길이 5 PeriodKey[] (공통 빠른버튼)
  arWindowYears: number;             // 미수 현장 펼침 시 매출·수금 조회 연수 (오늘 기준, 1~AR_WINDOW_MAX_YEARS)
}
/** 미수 드릴다운 조회 연수 허용 범위 */
export const AR_WINDOW_MIN_YEARS = 1;
export const AR_WINDOW_MAX_YEARS = 10;
export const DEFAULT_SALES_PREFS: SalesPrefs = {
  defaultView: "customer",
  defaultPeriod: "today",
  monthlyPeriod: "thisyear",   // 월판매현황 = 올해(월별 피벗 추이용). 설정에서 변경 가능
  receiptsPeriod: "today",
  customerPeriod: "all",       // 거래처 관리(사급) = 전체 누적
  customerPeriodGov: "2y",     // 거래처 관리(관급) = 최근 2년 (C023 전표 6,700건 → 약 1,000건)
  quickPresets: ["today", "week", "month", "lastweek", "lastmonth"],
  arWindowYears: 2,
};

/** 저장값 검증 — 1~AR_WINDOW_MAX_YEARS 정수, 벗어나면 기본값 */
function clampArYears(v: unknown): number {
  const n = Math.round(Number(v));
  if (!Number.isFinite(n)) return DEFAULT_SALES_PREFS.arWindowYears;
  return Math.min(AR_WINDOW_MAX_YEARS, Math.max(AR_WINDOW_MIN_YEARS, n));
}

export const salesPrefsRepo = {
  /** 본인 표시 설정 구독 (로그인 필요). 미로그인/문서없음 → 기본값 */
  watch(cb: (p: SalesPrefs) => void): () => void {
    const uid = auth.currentUser?.uid;
    if (!uid) { cb(DEFAULT_SALES_PREFS); return () => {}; }
    return onSnapshot(doc(db, "users", uid, "preferences", "sales"), (snap) => {
      const d = snap.data() ?? {};
      cb({
        defaultView: d.defaultView === "site" ? "site" : "customer",
        defaultPeriod: typeof d.defaultPeriod === "string" ? d.defaultPeriod : "today",
        monthlyPeriod: typeof d.monthlyPeriod === "string" ? d.monthlyPeriod : "thisyear",
        receiptsPeriod: typeof d.receiptsPeriod === "string" ? d.receiptsPeriod : DEFAULT_SALES_PREFS.receiptsPeriod,
        customerPeriod: typeof d.customerPeriod === "string" ? d.customerPeriod : DEFAULT_SALES_PREFS.customerPeriod,
        customerPeriodGov: typeof d.customerPeriodGov === "string" ? d.customerPeriodGov : DEFAULT_SALES_PREFS.customerPeriodGov,
        quickPresets: Array.isArray(d.quickPresets) ? d.quickPresets.map(String) : DEFAULT_SALES_PREFS.quickPresets,
        arWindowYears: clampArYears(d.arWindowYears),
      });
    }, () => cb(DEFAULT_SALES_PREFS));
  },
  async save(p: SalesPrefs): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid) throw new Error("로그인이 필요합니다.");
    await setDoc(doc(db, "users", uid, "preferences", "sales"), { ...p, updated_at: serverTimestamp() }, { merge: true });
  },
};
